import { BudgetLogSheet, DataTable, Dialog, FormLabel, Heading, Loader, Select } from "@/components";
import useBudgetMgmt from "./useBudgetMgmt";
import { Button, Input } from "@/components/ui";
import { Search } from "lucide-react";
import { CopyBudget } from "./CopyBudget";
import { BudgetBreakdown } from "./BudgetBreakdown";

const BudgetMgmt = () => {
  const {
    breadcrumbData,
    isMobile,
    onAdd,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    columns,
    budgetData,
    isBudgetDataLoading,
    openDeleteDialog,
    setOpenDeleteDialog,
    isDeletePending,
    onDeleteConfirm,
    rowSelected,
    setShowCopyDialog,
    showCopyDialog,
    onSubmitApproval,
    openApprovalDialog,
    setOpenApprovalDialog,
    isSubmitApprovalPending,
    openBudgetBreakdownDialog,
    setOpenBudgetBreakdownDialog,
    setOpenReviseDialog,
    openReviseDialog,
    onRevisedConfirm,
    openLogSheet,
    setOpenLogSheet,
    isMasterDataRoute,
    communityList,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    communityKey,
  } = useBudgetMgmt();

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={"Budget Management"}
            breadcrumbData={breadcrumbData}
          />
          {isMobile && (
            <Button onClick={onAdd} className="px-2">
              + Add Budget
            </Button>
          )}
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 mt-4 md:mt-auto">
          {isMasterDataRoute && (
            <div className="relative w-full sm:w-72 sm:min-w-72 sm:max-w-72 shrink-0">
              <FormLabel>Select Community</FormLabel>
              <Select
                key={communityKey}
                placeholder={""}
                searchPlaceholder={""}
                noDataPlaceholder={"Community No Found"}
                options={communityList?.data?.map((data: any) => ({
                  value: data?.communityId?.toString(),
                  label: data?.name,
                }))}
                value={communityId?.toString()}
                onChange={onCommunityChange}
                className="h-[36px] w-full"
                disabled={isCommunityLoading}
              />
            </div>
          )}
          <Input
            type="text"
            placeholder="Search"
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full sm:w-72 bg-white h-[40px]"
            startIcon={<Search className="w-4 h-4" />}
          />
          {!isMobile && (
              <Button onClick={onAdd}>+ Add Budget</Button>
            )}
        </div>
      </div>

      {/* Responsive Table */}
      {isBudgetDataLoading ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={(budgetData?.data || []).sort(
              (a: any, b: any) =>
                new Date(b.updatedAt).getTime() -
                new Date(a.updatedAt).getTime()
            )}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            onRowClick={onRowClick}
          />
          {/* Delete Dialog */}
          {openDeleteDialog && (
            <Dialog
              open={openDeleteDialog}
              setOpen={setOpenDeleteDialog}
              title={
                <div className="text-2xl font-bold text-primary">
                  {"Confirmation"}
                  <div className="h-1 w-[30%] bg-primary rounded-full" />
                </div>
              }
              saveText="Delete"
              saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
              isLoading={isDeletePending}
              onSave={onDeleteConfirm}
            >
              <p className="text-sm">
                Are you sure you want to delete the Annual Budget named -{" "}
                <span className="font-bold text-primary">
                  {rowSelected?.budgetName}
                </span>
                ? This action will remove all associated line items and cannot
                be undone.
              </p>
            </Dialog>
          )}
          {/* Approval Dialog */}
          {openApprovalDialog && (
            <Dialog
              open={openApprovalDialog}
              setOpen={setOpenApprovalDialog}
              title={
                <div className="text-2xl font-bold text-primary">
                  {"Confirmation"}
                  <div className="h-1 w-[30%] bg-primary rounded-full" />
                </div>
              }
              saveText="Yes"
              cancelText="No"
              isLoading={isSubmitApprovalPending}
              onSave={onSubmitApproval}
            >
              <p className="text-sm">
                You are about to submit the budget for approval. Once submitted,
                the budget will move to{" "}
                <b className="text-primary">'Pending for Approval'</b> status
                and cannot be edited until the approval process is completed. Do
                you want to proceed?
              </p>
            </Dialog>
          )}
        </>
      )}
      {showCopyDialog && (
        <CopyBudget
          setShowCopyDialog={setShowCopyDialog}
          showCopyDialog={showCopyDialog}
          copyVersionDetails={rowSelected}
        />
      )}
      <BudgetLogSheet
        open={openLogSheet}
        setOpen={setOpenLogSheet}
        communityId={rowSelected?.communityId}
        budget={rowSelected}
      />
      {openBudgetBreakdownDialog && (
        <BudgetBreakdown
          open={openBudgetBreakdownDialog}
          setOpen={setOpenBudgetBreakdownDialog}
          data={rowSelected}
        />
      )}
      {
        openReviseDialog &&
          <Dialog 
            open={openReviseDialog}
            setOpen={setOpenReviseDialog} 
            title={<div className="text-2xl font-bold text-primary">
              {"Revise Budget Confirmation !"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>}
            contentClassName="!lg:w-[1000px] !lg:max-w-[1000px]"
            saveText="Yes, Create Revision"
            onSave={onRevisedConfirm}
          >
            <div className="text-[14px]">
              <div className="italic mb-5">
                <b>You are about to revise an approved budget.</b>
                <br />
                <br />
                <ul className="list-disc pl-4">
                  <li>The current approved version will be archived.</li>
                  <li>A new version of the budget will be created.</li>
                  <li>Budget status will change to Draft.</li>
                  <li>Financial postings against this budget will be temporarily blocked until the revised budget is approved again by the Board of Members (BM)</li>
                </ul>
                <br/>
                <b>Restrictions:</b>
                <br />
                <br />
                <ul className="list-disc pl-4">
                  <li>Budget line items with posted financial transactions cannot be deleted.</li>
                  <li>Existing GL lines can be edited.</li>
                  <li>New GL lines can be added.</li>
                </ul>
              </div>
              
              <b>Do you want to continue?</b>
            </div> 
          </Dialog>
      }
    </div>
  );
};

export default BudgetMgmt;
